import React, { useState, useRef } from "react";
import { useFormikContext } from "formik";
import get from "lodash.get";
import { Dialog, Typography } from "@material-ui/core";
import styled from "styled-components";
import PropTypes from "prop-types";
import set from "lodash/set";
import { isEmpty, isEqual, cloneDeep } from "lodash";

import ModalTitle from "../layout/AppLayout/ModalDialog/ModalTitle";
import ActionButtons from "../forms/ActionButtons";
import {
  useCreateProcessRequests,
  useUpdateProcessRequests,
} from "../hooks/useProcessRequest";
import { openModalDialog } from "../../actions/layoutActions";
import useActions from "../hooks/useActions";
import { useDatacomponent } from "../hooks/useDatacomponent";
import { useModalStateContext } from "../layout/AppLayout/ModalDialog/withModalState";

import { RelatedRecords } from "./RelatedRecords";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 730px;
  padding: 24px;
  gap: 24px;
`;

const onError = setUniqueErrors => error => {
  const errors = get(error, "data.errors", []);

  const uniqueErrors = errors
    .filter(error => error.code == "unique" && error.source.includes("person"))
    .map(error => ({
      id: get(error, "detail.id"),
      personPath: error.source
        .replace(".email", "")
        .replace(/sites\[\d+\]\./, ""),
    }));
  setUniqueErrors(uniqueErrors);
};

// Freeze the contact subtree as first loaded, so we can later tell whether the
// SHARED person/contact data changed on save (vs. only per-relationship fields).
const useFrozenContact = (enabled, initialValues) => {
  const baselineRef = useRef(undefined);
  const contact = get(initialValues, "contact");
  if (enabled && baselineRef.current === undefined && !isEmpty(contact)) {
    baselineRef.current = cloneDeep(contact);
  }
  return baselineRef.current;
};

const hasSharedContactChanged = (enabled, baselineContact, values) => {
  if (!enabled || baselineContact === undefined) return true;
  return !isEqual(get(values, "contact"), baselineContact);
};

export const DuplicatePersonDialog = ({
  dataComponentId,
  contactRelationId,
  isUserForm,
  personPathReplace = "contact.person",
  onlyShowAffectedOnContactChange = false,
  ...props
}) => {
  const [uniqueErrors, setUniqueErrors] = useState([]);
  const [sendDisabled, setSendDisabled] = useState(true);

  const {
    values,
    initialValues,
    setFieldValue,
    handleSubmit,
  } = useFormikContext();
  const { setModalState, modalState } = useModalStateContext();
  const actions = useActions({
    openModalDialog,
  });

  // Per-relationship fields (isActive, portal access, ...) live at the root and
  // do NOT propagate to the other records sharing this person, so they must not
  // trigger the "Affected Records" modal.
  const baselineContact = useFrozenContact(
    onlyShowAffectedOnContactChange,
    initialValues
  );

  const onSubmitSuccess = () => {
    const personPath = get(
      props,
      "affectedPersonPath",
      isUserForm ? "personId" : "contact.personId"
    );
    const personId = get(values, personPath);
    const contactChanged = hasSharedContactChanged(
      onlyShowAffectedOnContactChange,
      baselineContact,
      values
    );
    setTimeout(() => {
      if (personId && contactChanged) {
        actions.openModalDialog("Affected Records", "AffectedRecordsDialog", {
          personId,
          contactRelationId,
          isUserForm,
        });
      }
    }, 0);
  };

  const dataComponent = useDatacomponent(dataComponentId);
  useCreateProcessRequests(dataComponent, {
    onError: onError(setUniqueErrors),
  });
  useUpdateProcessRequests(dataComponent, {
    onSuccess: onSubmitSuccess,
    onError: onError(setUniqueErrors),
  });

  const handleClose = () => {
    setUniqueErrors([]);
  };

  const handleConfirm = () => {
    handleClose();

    uniqueErrors.forEach(uniqueError => {
      const { id } = uniqueError;
      const personPath = get(
        props,
        "personPath",
        get(uniqueError, "personPath")
      );
      if (modalState.contacts) {
        const obj = { ...modalState };
        set(obj, `${personPath}.id`.replace("person", personPathReplace), id);
        set(
          obj,
          `${personPath}.id`
            .replace("person", personPathReplace)
            .replace("person.id", "personId"),
          id
        );
        setModalState(obj);
      } else {
        setFieldValue(`${personPath}.id`, id);
        setFieldValue(`${personPath}.id`.replace("person.id", "personId"), id);
      }
    });

    get(props, "handleSubmit", handleSubmit)();
  };

  return (
    <Dialog
      open={!isEmpty(uniqueErrors)}
      PaperProps={{
        elevation: 0,
        style: { maxWidth: "unset" },
      }}
    >
      <ModalTitle
        title={"Another record with the same email already exists"}
        width={"730px"}
        closeModalDialog={handleClose}
        isShowCloseIcon
      />
      <Content>
        <Typography>
          There is already one person with the entered email registered in the
          system. Would you like to use the same record?
        </Typography>
        {uniqueErrors.map(uniqueError => (
          <RelatedRecords
            key={uniqueError.id}
            personId={uniqueError.id}
            contactRelationId={contactRelationId}
            setSendDisabled={setSendDisabled}
            isUserForm={isUserForm}
          />
        ))}
        <Typography>
          If you select <b>Yes</b> then the above mentioned users/contacts will
          be updated with the information you provided on the form. If you would
          like to change that information before update, select <b>Cancel</b>{" "}
          and try again.
        </Typography>
        <ActionButtons
          onSend={handleConfirm}
          onCancel={handleClose}
          disabled={sendDisabled}
          sendButtonText="YES"
        />
      </Content>
    </Dialog>
  );
};
DuplicatePersonDialog.propTypes = {
  dataComponentId: PropTypes.string.isRequired,
  contactRelationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isUserForm: PropTypes.bool,
  personPathReplace: PropTypes.string,
  personPath: PropTypes.string,
  onlyShowAffectedOnContactChange: PropTypes.bool,
};
